import { useEffect, useMemo, useState } from "react";

import { Card } from "@/components/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetOrganisations } from "@/features/organisations/api/get-organisations";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { AverageRoomSystemSessionDuration } from "@/features/reporting/components/average-room-system-session-duration";
import { RoomSystemBaseAnalytics } from "@/features/reporting/components/room-system-base-analytics";
import { RoomSystemScheduledSessions } from "@/features/reporting/components/room-system-scheduled-sessions";
import { RoomSystemSessions } from "@/features/reporting/components/room-system-sessions";
import { useGetAdminRoomSystems } from "@/features/room-systems/api/get-admin-room-systems";

export function RoomSystemReporting() {
  const { data: organisations } = useGetOrganisations();
  const {
    data: roomSystems,
    isLoading: isLoadingRoomSystems,
    isSuccess: isSuccessRoomSystems,
  } = useGetAdminRoomSystems({});

  const [organisationId, setOrganisationId] = useState<string>("");
  const [roomSystemId, setRoomSystemId] = useState<string>("");

  const filteredRoomSystems = useMemo(() => {
    if (!isSuccessRoomSystems) return [];
    if (!organisationId) return roomSystems.items;
    return roomSystems.items.filter(
      (roomSystem) => roomSystem.organisation.id === organisationId,
    );
  }, [isSuccessRoomSystems, organisationId, roomSystems]);

  useEffect(() => {
    setRoomSystemId("");
  }, [organisationId]);
  return (
    <section>
      <OnboardingHeader
        title="Room Systems"
        description="View analytics for room systems for each organisation"
        className="mb-6"
        actions={
          <div className="flex gap-6">
            <Select
              value={organisationId}
              onValueChange={setOrganisationId}
              disabled={!organisations}
            >
              <SelectTrigger className="w-44 [&>span]:truncate">
                <SelectValue placeholder="Organisation/Group" />
              </SelectTrigger>
              <SelectContent>
                {organisations?.map((organisation) => (
                  <SelectItem key={organisation.id} value={organisation.id}>
                    {organisation.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={roomSystemId}
              onValueChange={setRoomSystemId}
              disabled={!isSuccessRoomSystems || isLoadingRoomSystems}
            >
              <SelectTrigger className="w-44 [&>span]:truncate">
                <SelectValue placeholder="Room System" />
              </SelectTrigger>
              <SelectContent>
                {filteredRoomSystems.map((roomSystem) => (
                  <SelectItem key={roomSystem.id} value={roomSystem.id}>
                    {roomSystem.name}
                  </SelectItem>
                ))}
                {isSuccessRoomSystems && !filteredRoomSystems.length && (
                  <p className="relative flex w-full select-none items-center rounded-sm px-2 py-1.5 text-sm text-muted outline-none">
                    No room systems found
                  </p>
                )}
              </SelectContent>
            </Select>
          </div>
        }
      />
      <div className="space-y-6">
        {!organisationId && !roomSystemId ? (
          <Card>
            <Card.Content>
              <div className="flex flex-col items-center justify-center gap-2">
                <span className="font-semibold text-foreground">
                  Select a group, organisation or an individual room system
                </span>
                <p className="text-center text-sm text-muted-foreground">
                  To view analytics for an organisation, group or room system,
                  select using the dropdowns provided.
                </p>
              </div>
            </Card.Content>
          </Card>
        ) : (
          <>
            <RoomSystemBaseAnalytics
              roomSystemId={roomSystemId || undefined}
              organisationId={organisationId || undefined}
            />
            <RoomSystemScheduledSessions
              organisationId={organisationId || undefined}
              roomSystemId={roomSystemId || undefined}
            />
            <AverageRoomSystemSessionDuration
              organisationId={organisationId || undefined}
              roomSystemId={roomSystemId || undefined}
            />
            <RoomSystemSessions
              organisationId={organisationId || undefined}
              roomSystemId={roomSystemId || undefined}
            />
          </>
        )}
      </div>
    </section>
  );
}
